import React, { useState } from "react";
import { i18n } from "../../translate/i18n";
import { 
    Avatar, 
    CardHeader, 
    Grid,
    Dialog,
    DialogContent,
    useMediaQuery,
    IconButton,
    Typography,
    Popover,
    Box,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";
import { useModernLayout } from "../../context/ModernLayout/ModernLayoutContext";

const useStyles = makeStyles((theme) => ({
    // Estilos para o modal da imagem
    imageModal: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    imageModalContent: {
        outline: "none",
        maxWidth: "90vw",
        maxHeight: "90vh",
    },
    expandedImage: {
        width: "100%",
        height: "auto",
        maxWidth: "500px",
        borderRadius: theme.spacing(1),
    },
    clickableAvatar: {
        cursor: "pointer",
        "&:hover": {
            opacity: 0.8,
        },
    },
    cardHeader: {
        padding: '4px 0',
        flex: 1,
        minWidth: 0,
        '& .MuiCardHeader-content': {
            overflow: 'hidden',
            minWidth: 0,
            flex: 1,
        },
        '& .MuiCardHeader-title': {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: '0.875rem',
            fontWeight: 500,
            maxWidth: '100%',
        },
        '& .MuiCardHeader-subheader': {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: '0.75rem',
            maxWidth: '100%',
        },
        '& .MuiCardHeader-avatar': {
            marginRight: 8,
        },
    },
    compactWrapper: {
        display: 'flex',
        alignItems: 'center',
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        width: '100%',
    },
    moreButton: {
        padding: 2,
        marginLeft: 2,
        minWidth: 'auto',
        flexShrink: 0,
    },
    infoPopover: {
        padding: theme.spacing(2),
        maxWidth: 300,
    },
    infoTitle: {
        fontWeight: 600,
        marginBottom: theme.spacing(0.5),
    },
    infoSubtitle: {
        color: theme.palette.text.secondary,
        fontSize: '0.85rem',
        lineHeight: 1.4,
    },
}));

const TicketInfo = ({ contact, ticket, onClick }) => {
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isModernLayout = useModernLayout();
    const [imageModalOpen, setImageModalOpen] = useState(false); // Estado para o modal da imagem
    const [infoAnchorEl, setInfoAnchorEl] = useState(null); // Estado para o popover de info
    
    // Aplicar estilos compactos apenas no Modern Layout + Mobile
    const useCompactStyle = isMobile && isModernLayout;
    
    // Handlers para o popover de informações
    const handleInfoClick = (event) => {
        event.stopPropagation();
        setInfoAnchorEl(event.currentTarget);
    };
    
    const handleInfoClose = () => {
        setInfoAnchorEl(null);
    };
    
    const infoPopoverOpen = Boolean(infoAnchorEl);

    // Função para abrir modal da imagem
    const handleImageClick = (e) => {
        e.stopPropagation(); // Prevenir que o clique no avatar execute outros handlers
        if (contact?.urlPicture) {
            setImageModalOpen(true);
        }
    };

    // Função para fechar modal da imagem
    const handleImageModalClose = () => {
        setImageModalOpen(false);
    };

    const renderCardReader = () => {
        // Texto completo do título
        const fullTitle = `${contact?.name || '(sem contato)'} #${ticket?.id}`;
        
        // Texto completo do subtítulo
        const subheaderParts = [
            ticket?.user && `${i18n.t("messagesList.header.assignedTo")} ${ticket?.user?.name}`,
            contact?.contactWallets && contact.contactWallets.length > 0
                ? `• ${i18n.t("wallets.wallet")}: ${contact.contactWallets[0].wallet?.name || 'N/A'}`
                : null
        ].filter(Boolean);
        const fullSubheader = subheaderParts.join(' ');

        // Layout compacto apenas para Modern Layout + Mobile
        if (useCompactStyle) {
            return (
                <div className={classes.compactWrapper}>
                    <CardHeader
                        onClick={onClick}
                        style={{ cursor: "pointer", flex: 1, minWidth: 0 }}
                        className={classes.cardHeader}
                        titleTypographyProps={{ noWrap: true }}
                        subheaderTypographyProps={{ noWrap: true }}
                        avatar={
                            <Avatar 
                                src={contact?.urlPicture} 
                                alt="contact_image" 
                                className={classes.clickableAvatar}
                                onClick={handleImageClick}
                                style={{ width: 36, height: 36 }}
                            />
                        }
                        title={fullTitle}
                        subheader={fullSubheader}
                    />
                    <IconButton 
                        className={classes.moreButton}
                        onClick={handleInfoClick}
                        size="small"
                        title="Ver informações completas"
                    >
                        <MoreHorizIcon fontSize="small" />
                    </IconButton>
                    
                    {/* Popover com informações completas */}
                    <Popover
                        open={infoPopoverOpen}
                        anchorEl={infoAnchorEl}
                        onClose={handleInfoClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                        }}
                    >
                        <Box className={classes.infoPopover}>
                            <Box display="flex" alignItems="center" marginBottom={1}>
                                <Avatar 
                                    src={contact?.urlPicture} 
                                    alt="contact_image"
                                    style={{ width: 48, height: 48, marginRight: 12 }}
                                    onClick={handleImageClick}
                                    className={classes.clickableAvatar}
                                />
                                <Box>
                                    <Typography className={classes.infoTitle}>
                                        {contact?.name || '(sem contato)'}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        Ticket #{ticket?.id}
                                    </Typography>
                                </Box>
                            </Box>
                            {fullSubheader && (
                                <Typography className={classes.infoSubtitle}>
                                    {fullSubheader}
                                </Typography>
                            )}
                            {contact?.number && (
                                <Typography className={classes.infoSubtitle} style={{ marginTop: 4 }}>
                                    📱 {contact.number}
                                </Typography>
                            )}
                        </Box>
                    </Popover>
                </div>
            );
        }

        // Layout padrão (Classic e Desktop)
        return (
            <CardHeader
                onClick={onClick}
                style={{ cursor: "pointer" }}
                titleTypographyProps={{ noWrap: true }}
                subheaderTypographyProps={{ noWrap: true }}
                avatar={
                    <Avatar 
                        src={contact?.urlPicture} 
                        alt="contact_image" 
                        className={classes.clickableAvatar}
                        onClick={handleImageClick}
                    />
                }
                title={`${(contact?.name && contact.name.length > 12) ? 
                    `${contact.name.substring(0, 12)}...` : 
                    contact?.name || '(sem contato)'} #${ticket?.id}`}
                subheader={fullSubheader}
            />
        );
    }

    return (
        <React.Fragment>
            <Grid container alignItems="center" spacing={useCompactStyle ? 0 : 10}>
                {/* Conteúdo do contato - largura maior no modo compacto */}
                <Grid item xs={useCompactStyle ? 10 : 6}>
                    {renderCardReader()}
                </Grid>
            </Grid>

            {/* Modal da Imagem */}
            <Dialog
                open={imageModalOpen}
                onClose={handleImageModalClose}
                className={classes.imageModal}
                maxWidth="md"
                fullWidth
            >
                <DialogContent className={classes.imageModalContent}>
                    <img 
                        src={contact?.urlPicture} 
                        alt={contact?.name || "Foto do contato"}
                        className={classes.expandedImage}
                    />
                </DialogContent>
            </Dialog>
        </React.Fragment>
    );
};

export default TicketInfo;
